import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography, 
  Box,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import { useAppSelector } from 'src/store';
import { useSnackbar } from 'notistack';
import ChannelPicker from '../Channels/ChannelPicker';
import { selectContactById } from 'src/features/account/contacts.slice';

interface ContactEmailDialogProps {
  open: boolean;
  contactId: string;
  onClose: () => void;
}

const ContactEmailDialog: React.FC<ContactEmailDialogProps> = ({ 
  open, 
  contactId, 
  onClose 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Get contact from store using selector
  const contact = useAppSelector(state => selectContactById(state, contactId));
  const contactLoading = useAppSelector(state => 
    state.contacts.loadingContactsIds.includes(contactId)
  );

  // State for form fields
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  // State for preparing email
  const [isLoading, setIsLoading] = useState(false);

  // Reset form state when dialog opens
  useEffect(() => {
    if (open) {
      setSubject('');
      setBody('');
      setIsLoading(false);
    }
  }, [open]);

  // Handle opening the mail app
  const handleOpenMailApp = async () => {
    if (!selectedChannel) {
      enqueueSnackbar('Please select a channel', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    
    try {
      // Custom encoding that preserves spaces and handles special characters properly
      const customEncode = (str: string) => {
        return encodeURIComponent(str)
          .replace(/%20/g, ' ')     // Keep spaces as spaces
          .replace(/%0A/g, '%0D%0A') // Proper line breaks for email
          .replace(/'/g, '%27')     // Encode single quotes
          .replace(/"/g, '%22');    // Encode double quotes
      };

      // Build mailto URL manually
      const params = [];
      
      if (contact?.email) {
        params.push(`to=${customEncode(contact.email)}`);
      }
      if (selectedChannel?.email_address) {
        params.push(`cc=${customEncode(selectedChannel.email_address)}`);
      }
      if (subject.trim()) {
        params.push(`subject=${customEncode(subject.trim())}`);
      }
      if (body.trim()) {
        params.push(`body=${customEncode(body.trim())}`);
      }

      const mailtoUrl = `mailto:?${params.join('&')}`;
      
      // Open the default mail app
      window.location.href = mailtoUrl;
      
      enqueueSnackbar('Opening mail app...', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Error opening mail app:', error);
      enqueueSnackbar('Failed to open mail app', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (contactLoading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Email {contact?.name || contact?.email || 'Contact'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Stack spacing={2}>
          <ChannelPicker
            selectedChannel={selectedChannel}
            setSelectedChannel={setSelectedChannel}
            inputType="email"
          />

          {selectedChannel && (
            <>
              <TextField
                label="Subject"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What's this about?"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '14px'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                    '&.Mui-focused, &.MuiFormLabel-filled': {
                      fontSize: '12px'
                    }
                  }
                }}
              />

              {(subject.trim() || body.trim()) && (
                <TextField
                  label="Message"
                  multiline
                  rows={3}
                  fullWidth
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Optional message..."
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        fontSize: '12px'
                      }
                    }
                  }}
                />
              )}

              {/* Minimal Preview */}
              {contact?.email && (
                <Box sx={{ 
                  px: 2, 
                  py: 1, 
                  bgcolor: 'action.hover', 
                  borderRadius: 1,
                  fontSize: '13px'
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    To: {contact.email} • CC: {selectedChannel.email_address}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
          size="small"
          sx={{ fontSize: '13px' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleOpenMailApp}
          disabled={!selectedChannel || isLoading}
          variant="contained" 
          size="small"
          sx={{ 
            minWidth: 100,
            fontSize: '13px',
            height: '32px'
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            'Open Mail'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactEmailDialog;